import {SearchResult} from 'priority-web-sdk';
import {useState, useEffect, useCallback} from 'react';
import {SearchState} from './useSearch';
import {getItemValue, convertItemToTag} from '../utils/search';
import {keys} from '../../../../constants/KeyCodes';

function getRange(currentIndex, lastSelectedIndex, results) {
    const isSelectionDown = currentIndex > lastSelectedIndex;
    const startIndex = isSelectionDown ? lastSelectedIndex + 1 : currentIndex;
    const endIndex = (isSelectionDown ? currentIndex : lastSelectedIndex) + 1;
    const selectedRange = results.slice(startIndex, endIndex);
    return selectedRange.map(i => convertItemToTag(i));
}

function getRangeSelected(item, selected, results) {
    let nextSelected;
    const value = getItemValue(item);
    const isDelete = selected.findIndex(i => i.value === value) > -1;

    // get range
    const currentIndex = results.indexOf(item);
    const lastSelected = selected[selected.length - 1];
    const lastSelectedIndex = results.findIndex(item => getItemValue(item) === lastSelected.value);
    const selectedRange = getRange(currentIndex, lastSelectedIndex, results);

    if (isDelete) {
        //on delete get all the items not in the selectedRange
        nextSelected = selected.filter(
            value => selectedRange.findIndex(i => i.value === value.value) === -1
        );
    } else {
        nextSelected = [...selected, ...selectedRange];
    }
    return nextSelected;
}

export type Selected = {value; desc}[];
type OnSelect = (value: string) => void;
type OnMultiSelect = (selected: Selected) => void;

export interface SelectActions {
    onSelect?: OnSelect;
    onMultiSelect?: OnMultiSelect;
}

export interface SelectedLimit {
    limit?: number;
    limitCallback?: () => void;
}
export interface SelectHandlers {
    handleSelectItem: (item: SearchResult, event: KeyboardEvent | MouseEvent) => void;
    handleClearItem: (item: SearchResult) => void;
    handleMultiSelect: () => void;
    handleSelectKeys: (event: KeyboardEvent) => void;
    handleResetItems: (items?: string[]) => void;
}

export type IsItemSelected = (item: SearchResult) => boolean;

export const useSelect = (
    searchState: Partial<SearchState>,
    actions: SelectActions,
    selectedLimit?: SelectedLimit
) => {
    const {onSelect, onMultiSelect} = actions;
    const [selected, setSelected] = useState<Selected>([]);

    useEffect(() => {
        if (
            !searchState.isLoading &&
            selected.length === 0 &&
            searchState.searchQuery &&
            searchState.isMulti
        ) {
            setSelected([]);
        }
    }, [searchState.isLoading]);

    const isItemSelected = useCallback(
        item => {
            const value = getItemValue(item);
            return selected.findIndex(t => t.value === value) > -1;
        },
        [selected]
    );

    const handleSelectItem = useCallback(
        (item, event) => {
            event.stopPropagation();
            if (searchState.isMulti) {
                let nextSelected = [...selected];
                // Enter key shouldn't deselect an item
                if (event.key !== keys.ENTER || !isItemSelected(item)) {
                    if (event.shiftKey && selected.length) {
                        nextSelected = getRangeSelected(item, selected, searchState.results);
                        if (selectedLimit && nextSelected.length > selectedLimit.limit) {
                            selectedLimit.limitCallback();
                            return;
                        }
                    } else {
                        const index = selected.findIndex(i => getItemValue(item) === i.value);
                        if (index > -1) {
                            nextSelected.splice(index, 1);
                        } else {
                            if (selectedLimit && selected.length >= selectedLimit.limit) {
                                selectedLimit.limitCallback();
                                return;
                            }

                            const tag = convertItemToTag(item);
                            nextSelected.push(tag);
                        }
                    }
                    setSelected(nextSelected);
                }
                if (event.key === keys.ENTER) {
                    setTimeout(() => onMultiSelect(nextSelected.map(i => i.value)), 300);
                }
            } else {
                //when it's not multiselect, use onSelect handler to process the selected value
                const value = getItemValue(item);
                onSelect(value);
            }
        },
        [
            searchState.results,
            searchState.isMulti,
            selected,
            isItemSelected,
            onMultiSelect,
            onSelect,
            selectedLimit
        ]
    );

    const handleMultiSelect = () => {
        onMultiSelect(selected.map(i => i.value));
    };

    const handleSelectKeys = (event: KeyboardEvent) => {
        if (event.key === keys.ENTER || (searchState.isMulti && event.key === keys.SPACE)) {
            const {focusedIndex, results} = searchState;
            if (focusedIndex > -1) {
                event.key === keys.ENTER;
                event.preventDefault();
                const item = results[focusedIndex];
                item && handleSelectItem(item, event);
            }
        }
    };

    const handleClearItem = useCallback(
        index => {
            if (index > -1) {
                const nextSelected = [...selected];
                nextSelected.splice(index, 1);
                setSelected(nextSelected);
            }
        },
        [selected]
    );

    const handleResetItems = useCallback(
        (selected = []) => {
            if (selectedLimit && selected.length > selectedLimit.limit) {
                selectedLimit.limitCallback();
                return;
            }
            setSelected(selected);
        },
        [selectedLimit]
    );

    return {
        selected,
        isItemSelected,
        selectHandlers: {
            handleSelectItem,
            handleClearItem,
            handleMultiSelect,
            handleResetItems,
            handleSelectKeys
        }
    };
};
